import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Input } from "@/components/input";
import { LoadingSpinner } from "@/components/loading-spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/table";
import { useJiraMetricsTickets } from "@/http/api-hooks";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { formatDuration, formatShortDate } from "./formatters";
import { TicketGanttChart } from "./ticket-gantt-chart";

type Props = {
  from?: string;
  to?: string;
  projects?: string;
  selectedTicketKey?: string;
};

export function TicketDetailTable({ from, to, projects, selectedTicketKey }: Props) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [expandedTicketIds, setExpandedTicketIds] = useState<Record<number, boolean>>({});
  const pageSize = 20;
  const shouldFetch = Boolean(from && to);

  const {
    data: ticketsResponse,
    isLoading,
    isValidating,
    mutate,
    error,
  } = useJiraMetricsTickets(
    {
      from,
      to,
      done_only: true,
      search: search || undefined,
      page,
      page_size: pageSize,
      sort_by: "moved_to_done",
      sort_order: "desc",
      projects,
    },
    shouldFetch,
  );

  const tickets = ticketsResponse?.data ?? [];
  const ticketsMeta = ticketsResponse?.meta;

  useEffect(() => {
    setPage(1);
  }, [from, to]);

  useEffect(() => {
    if (!selectedTicketKey) {
      return;
    }
    setSearch(selectedTicketKey);
    setPage(1);
  }, [selectedTicketKey]);

  const toggleTicketExpansion = (ticketId: number) => {
    setExpandedTicketIds(previous => ({
      ...previous,
      [ticketId]: !previous[ticketId],
    }));
  };

  return (
    <Card id="ticket-detail-table">
      <CardHeader>
        <CardTitle>Ticket Detail Table</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="w-full max-w-sm">
            <Input
              value={search}
              onChange={event => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search by key, summary, assignee..."
            />
          </div>
          <Button variant="outline" onClick={() => mutate()}>
            Refresh
          </Button>
        </div>
        {error && <div className="mb-3 text-sm text-red-600">Failed to load tickets table.</div>}
        {isLoading && !ticketsResponse && (
          <div className="mb-3">
            <LoadingSpinner />
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>Ticket</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead>Assignee</TableHead>
              <TableHead>First In Progress</TableHead>
              <TableHead>Moved to Done</TableHead>
              <TableHead>In Progress</TableHead>
              <TableHead>Review</TableHead>
              <TableHead>Cycle</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tickets.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground">
                  No tickets found for selected filters.
                </TableCell>
              </TableRow>
            )}
            {tickets.map(ticket => {
              const isExpanded = Boolean(expandedTicketIds[ticket.id]);

              return (
                <Fragment key={ticket.id}>
                  <TableRow>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleTicketExpansion(ticket.id)}
                        aria-label={isExpanded ? "Collapse ticket gantt" : "Expand ticket gantt"}
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium">
                      <a
                        href={`https://remerge.atlassian.net/browse/${ticket.ticket_key}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sky-600 hover:text-sky-700 hover:underline"
                      >
                        {ticket.ticket_key}
                      </a>
                    </TableCell>
                    <TableCell className="max-w-xs truncate" title={ticket.summary || undefined}>
                      {ticket.summary || "-"}
                    </TableCell>
                    <TableCell>{ticket.assignee || "-"}</TableCell>
                    <TableCell>{formatShortDate(ticket.first_time_in_progress)}</TableCell>
                    <TableCell>{formatShortDate(ticket.moved_to_done)}</TableCell>
                    <TableCell>{formatDuration(ticket.total_time_in_progress_seconds)}</TableCell>
                    <TableCell>{formatDuration(ticket.total_time_in_review_seconds)}</TableCell>
                    <TableCell>{formatDuration(ticket.cycle_time_seconds)}</TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow className="bg-muted/30">
                      <TableCell colSpan={9} className="p-2 whitespace-normal">
                        <TicketGanttChart ticket={ticket} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-muted-foreground">
            Page {ticketsMeta?.page || 1} / {ticketsMeta?.total_page || 1} ({ticketsMeta?.total || 0} tickets)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={!ticketsMeta || ticketsMeta.page <= 1}
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={!ticketsMeta || ticketsMeta.page >= ticketsMeta.total_page}
              onClick={() => setPage(prev => prev + 1)}
            >
              Next
            </Button>
            {isValidating && <span className="self-center text-xs text-muted-foreground">Updating...</span>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
